import {
  type Consultation,
  RXDB_CONSULTATION_COLLECTION_NAME,
  useRxCollectionConsultation,
} from "$global-providers/rxdb-provider/collections/consultation";
import {
  type Disease,
  RXDB_DISEASE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/disease";
import {
  type ImagingStudy,
  useRxCollectionImagingStudy,
} from "$global-providers/rxdb-provider/collections/imaging-study";
import {
  RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
  type InvestigationActivity as RxInvestigationActivity,
} from "$global-providers/rxdb-provider/collections/investigation-activity";
import {
  type Prescription,
  RXDB_PRESCRIPTION_COLLECTION_NAME,
  useRxCollectionPrescription,
} from "$global-providers/rxdb-provider/collections/prescription";
import { useUser } from "$global-providers/user-provider";
import { UnsyncedToast } from "$routes/_lib/layout/unsynced-toast";
import { BasicDetails } from "$routes/patient/[:patientId]/_lib/patient-centre/basic-details";
import { VaccinationHistoryCard } from "$routes/patient/[:patientId]/_lib/vaccination-history-card";
import { allVaccineCodesToDetailsMap } from "$routes/patient/[:patientId]/_lib/vaccinations-service/vaccination-schedule";
import { ImagingStudyCard } from "$routes/patient/[:patientId]/overview/history/_lib/imaging-study-card";
import { usePatientImagingStudies } from "$routes/patient/[:patientId]/overview/history/_lib/use-patient-imaging-studies";
import { CircularProgress, styled } from "@material-ui/core";
import { subDays } from "date-fns";
import { Gender } from "elephant-web/gql";
import { makeConsultation } from "elephant-web/test/fixtures/rxdbConsultation";
import { makePrescription } from "elephant-web/test/fixtures/rxdbPrescription";
import { useParams } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { InvestigationActivityOutcome } from "../../../../patient/[:patientId]/investigations/_lib/investigation-activity-outcome";

const Content = styled("div")(() => ({
  maxWidth: "800px",
  marginInline: "auto",
}));

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  paddingTop: "32px",
}));

export const PatientHistoryPage = () => {
  const { patientId } = useParams();
  const { id: userId, facilityId } = useUser();

  const prescriptionCollection = useRxCollectionPrescription();
  const consultationCollection = useRxCollectionConsultation();
  const imagingStudyCollection = useRxCollectionImagingStudy();

  const { result: vaccines } = useRxData<Disease>(
    RXDB_DISEASE_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          $or: [
            {
              "catalogueCoding.icd11.code": {
                $in: Object.keys(allVaccineCodesToDetailsMap),
              },
            },
            {
              "catalogueCoding.icd10.code": {
                $in: Object.keys(allVaccineCodesToDetailsMap),
              },
            },
          ],
        },
      }),
  );
  const addMockPrescription = async () => {
    const mockPrescription = makePrescription({
      patientId,
      prescribedByUserId: userId,
      facilityId,
      meta: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdByUserId: userId,
        updatedByUserId: userId,
      },
    });
    await prescriptionCollection.upsert(mockPrescription);
  };

  const addMockConsultation = async () => {
    const mockConsultation = makeConsultation({
      patientId,
      facilityId,
      symptomIds: ["Fever"],
    });
    await consultationCollection.upsert(mockConsultation);
  };

  const investigationActivities = useRxData<RxInvestigationActivity>(
    RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          patientId: {
            $eq: patientId,
          },
        },
        sort: [{ "meta.updatedAt": "desc" }],
        limit: 20,
      }),
  );

  const { result: prescriptions } = useRxData<Prescription>(
    RXDB_PRESCRIPTION_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: { patientId: { $eq: patientId } },
        sort: [{ "meta.updatedAt": "desc" }],
        limit: 5,
      }),
  );

  const { result: consultations } = useRxData<Consultation>(
    RXDB_CONSULTATION_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: { patientId: { $eq: patientId } },
        sort: [{ "meta.updatedAt": "desc" }],
        limit: 20,
      }),
  );

  const imagingStudies = usePatientImagingStudies(patientId!);

  const addMockVaccination = async () => {
    const mockConsultation = makeConsultation({
      patientId,
      facilityId,
      isImmunisation: true,
      isPerformedAtOtherClinic: false,
      diseaseIds: [
        vaccines?.find((v) => v.id === "IMM1")?.id,
        vaccines?.find((v) => v.id === "IMM3")?.id,
      ],
      backdatedTo: subDays(new Date(), 1).toISOString(),
      meta: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdByUserId: userId,
        updatedByUserId: userId,
      },
    });
    await consultationCollection.upsert(mockConsultation);
  };

  const addMockImagingStudy = async () => {
    const timestamp = Date.now();
    const mockImagingStudy: ImagingStudy = {
      id: `imaging-${timestamp}`,
      elephantPatientId: patientId,
      accessionNumber: `ACCESSION-${timestamp}`,
      meta: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdByUserId: userId,
        updatedByUserId: userId,
      },
    };
    await imagingStudyCollection?.upsert(mockImagingStudy);
  };

  const vaccinations = consultations
    .filter((consultation) => consultation.isImmunisation)
    .slice(0, 5);

  if (!investigationActivities.result)
    return (
      <span>
        Pulling investigations..
        <CircularProgress />
      </span>
    );
  return (
    <Content>
      <Container>
        <BasicDetails
          name="John Doe"
          dob={new Date("1990-01-01")}
          sex={Gender.M}
        />
        <UnsyncedToast />
        <button type="button" onClick={addMockImagingStudy}>
          Add Mock Imaging Study
        </button>
        {imagingStudies.studies.map((study) => (
          <ImagingStudyCard key={study.id} study={study} />
        ))}
        <button type="button" onClick={addMockConsultation}>
          Add Mock Consultation
        </button>
        {consultations.slice(0, 5).map((consultation) => {
          return (
            <div key={consultation.id}>
              {consultation.id} {consultation.meta.createdAt}
            </div>
          );
        })}
        <button type="button" onClick={addMockPrescription}>
          Add Mock Prescription
        </button>
        {prescriptions.map((prescription) => {
          return (
            <div key={prescription.id}>
              {prescription.id} {prescription.meta.createdAt}
            </div>
          );
        })}
        {investigationActivities.result?.map(
          ({ _data: investigationActivity }, index) => {
            return (
              <InvestigationActivityOutcome
                key={index}
                investigationActivity={investigationActivity}
              />
            );
          },
        )}
        <button type="button" onClick={addMockVaccination}>
          Add Mock Vaccination
        </button>
        {vaccinations.map((vaccination) => {
          return (
            <VaccinationHistoryCard
              key={vaccination.id}
              consultation={{
                uuid: vaccination.id,
                meta: {
                  created: vaccination.meta.createdAt,
                  createdByUserId: vaccination.meta.createdByUserId,
                },
                diseases: vaccination.diseaseIds.map((id) => ({ id })),
                backdatedTo: vaccination.backdatedTo,
                isPerformedAtOtherClinic: vaccination.isPerformedAtOtherClinic,
              }}
            />
          );
        })}
      </Container>
    </Content>
  );
};
